import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, X, Save, AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllNewJobCatalogRows, newJobCatalogQuery } from '@/lib/catalog/newJobCatalogQuery';
import { toast } from '@/hooks/use-toast';
import { useProvisionalOptimizer } from '@/hooks/useProvisionalOptimizer';
import { ResolutionOptionsModal } from '@/components/optimizer/ResolutionOptionsModal';

interface Job {
  category: string;
  submenu: string;
  name: string;
  minutes: number;
  customHours?: number;
  notes?: string;
  bay: string;
  skill: number;
}

interface EditJobsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobs: Job[];
  onSave: (jobs: Job[]) => void;
  ticketId?: string; // Optional: for scope expansion detection
}

export const EditJobsModal = ({ open, onOpenChange, jobs, onSave, ticketId }: EditJobsModalProps) => {
  const [editedJobs, setEditedJobs] = useState<Job[]>(jobs);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [submenus, setSubmenus] = useState<string[]>([]);
  const [selectedSubmenu, setSelectedSubmenu] = useState<string>('');
  const [availableJobs, setAvailableJobs] = useState<any[]>([]);
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [provisionalThreshold, setProvisionalThreshold] = useState<number>(0);

  // Provisional optimizer for scope expansion detection
  const {
    isLoading: isCheckingScope,
    result: scopeResult,
    options: resolutionOptions,
    selectedOption,
    checkScopeExpansion,
    selectOption,
    applySelectedOption,
    reset: resetOptimizer,
  } = useProvisionalOptimizer();

  // Calculate added minutes from job changes
  const originalTotalMinutes = useMemo(() => 
    jobs.reduce((sum, job) => sum + job.minutes, 0), [jobs]
  );
  
  const currentTotalMinutes = useMemo(() => 
    editedJobs.reduce((sum, job) => sum + job.minutes, 0), [editedJobs]
  );

  const addedMinutes = currentTotalMinutes - originalTotalMinutes;
  const hasScopeExpansion = addedMinutes > 0;

  // Get the scope expansion issue if detected
  const scopeIssue = scopeResult?.issuesDetected.find(
    issue => issue.type === 'SCOPE_EXPANDED'
  ) ?? null;

  useEffect(() => {
    if (open) {
      setEditedJobs(jobs);
      loadCategories();
      loadProvisionalThreshold();
    }
  }, [open, jobs]);

  const loadProvisionalThreshold = async () => {
    try {
      const { data } = await supabase
        .from('shop_profile')
        .select('shop_culture')
        .limit(1)
        .single();
      
      const culture = data?.shop_culture as Record<string, unknown> | null;
      const optimizerSettings = culture?.optimizer_settings as Record<string, unknown> | null;
      const threshold = optimizerSettings?.provisional_threshold_minutes as number | undefined;
      setProvisionalThreshold(threshold ?? 0);
    } catch (err) {
      console.error('Error loading provisional threshold:', err);
      setProvisionalThreshold(0); // Default to triggering on any increase
    }
  };

  const loadCategories = async () => {
    const { data } = await fetchAllNewJobCatalogRows<{ category_key: string }>('category_key', ['category_key']);
    
    if (data) {
      const uniqueCategories = [...new Set(data.map(item => item.category_key))];
      setCategories(uniqueCategories);
    }
  };

  useEffect(() => {
    const fetchSubmenus = async () => {
      if (!selectedCategory) {
        setSubmenus([]);
        setSelectedSubmenu('');
        setAvailableJobs([]);
        return;
      }

      const { data } = await newJobCatalogQuery('sub_menu')
        .eq('category_key', selectedCategory)
        .order('sub_menu');
      
      if (data) {
        const uniqueSubmenus = [...new Set(data.map(item => item.sub_menu))];
        setSubmenus(uniqueSubmenus);
      }
    };
    fetchSubmenus();
  }, [selectedCategory]);

  useEffect(() => {
    const fetchJobs = async () => {
      if (!selectedCategory || !selectedSubmenu) {
        setAvailableJobs([]);
        return;
      }

      const { data } = await newJobCatalogQuery('*')
        .eq('category_key', selectedCategory)
        .eq('sub_menu', selectedSubmenu)
        .order('label');

      if (data) {
        setAvailableJobs(data);
      }
    };
    fetchJobs();
  }, [selectedCategory, selectedSubmenu]);

  const handleAddJob = () => {
    if (!selectedJob) {
      toast({
        title: "No job selected",
        description: "Please select a job to add",
        variant: "destructive"
      });
      return;
    }

    const skillLevelMap: Record<string, number> = {
      'basic': 1,
      'intermediate': 2,
      'advanced': 3,
      'expert': 4,
    };

    const newJob: Job = {
      category: selectedJob.category_key,
      submenu: selectedJob.sub_menu,
      name: selectedJob.label,
      minutes: selectedJob.minutes_default,
      bay: 'Any',
      skill: skillLevelMap[selectedJob.skill_level?.toLowerCase()] || 2,
    };

    setEditedJobs([...editedJobs, newJob]);
    setSelectedCategory('');
    setSelectedSubmenu('');
    setSelectedJob(null);
  };

  const handleRemoveJob = (index: number) => {
    // Remove job from local state only (pre-import CSV editing context)
    // No pensieve logging here since this is not a real ticket - it's pre-import editing
    setEditedJobs(editedJobs.filter((_, i) => i !== index));
  };

  const handleUpdateJob = (index: number, field: keyof Job, value: any) => {
    const updated = [...editedJobs];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'customHours') {
      updated[index].minutes = Math.round(value * 60);
    }
    setEditedJobs(updated);
  };

  const handleSave = async () => {
    // If there's scope expansion exceeding threshold and we have a ticketId, check for impacts
    if (ticketId && addedMinutes > provisionalThreshold) {
      setPendingSave(true);
      const result = await checkScopeExpansion(ticketId, addedMinutes);
      
      if (result?.issuesDetected.some(i => i.type === 'SCOPE_EXPANDED') && 
          result.resolutionOptions.length > 0) {
        // Show resolution modal
        setShowResolutionModal(true);
        return;
      }
    }
    
    // No scope expansion issues or no ticketId, save directly
    completeSave();
  };

  const completeSave = () => {
    onSave(editedJobs);
    onOpenChange(false);
    resetOptimizer();
    setPendingSave(false);
  };

  const handleResolutionApply = async () => {
    const success = await applySelectedOption();
    if (success) {
      setShowResolutionModal(false);
      completeSave();
    }
    return success;
  };

  const handleResolutionSkip = () => {
    setShowResolutionModal(false);
    completeSave();
  };

  const handleModalClose = (open: boolean) => {
    if (!open) {
      resetOptimizer();
      setPendingSave(false);
    }
    setShowResolutionModal(open);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Jobs</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add New Job Section */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-semibold">Add New Job</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Submenu</Label>
                <Select value={selectedSubmenu} onValueChange={setSelectedSubmenu} disabled={!selectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select submenu" />
                  </SelectTrigger>
                  <SelectContent>
                    {submenus.map((sub) => (
                      <SelectItem key={sub} value={sub}>
                        {sub}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Job</Label>
                <Select value={selectedJob?.id} onValueChange={(val) => setSelectedJob(availableJobs.find(j => j.id === val))} disabled={!selectedSubmenu}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select job" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableJobs.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleAddJob} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Job
            </Button>
          </div>

          {/* Scope Expansion Warning */}
          {hasScopeExpansion && ticketId && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Scope Expansion: +{addedMinutes} minutes
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Schedule impacts will be checked when you save
                </p>
              </div>
            </div>
          )}

          {/* Current Jobs List */}
          <div className="space-y-3">
            <h3 className="font-semibold">Current Jobs ({editedJobs.length})</h3>
            {editedJobs.map((job, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium">{job.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {job.category} / {job.submenu}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveJob(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Hours</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={job.customHours || job.minutes / 60}
                      onChange={(e) => handleUpdateJob(index, 'customHours', parseFloat(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Bay Requirement</Label>
                    <Select 
                      value={job.bay} 
                      onValueChange={(val) => handleUpdateJob(index, 'bay', val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Any">Any</SelectItem>
                        <SelectItem value="General">General</SelectItem>
                        <SelectItem value="Alignment">Alignment</SelectItem>
                        <SelectItem value="Diagnostic">Diagnostic</SelectItem>
                        <SelectItem value="Tire">Tire</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}

            {editedJobs.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No jobs added yet
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pendingSave}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={pendingSave || isCheckingScope}>
              {(pendingSave || isCheckingScope) ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Resolution Options Modal */}
        <ResolutionOptionsModal
          open={showResolutionModal}
          onOpenChange={handleModalClose}
          issue={scopeIssue}
          options={resolutionOptions}
          selectedOption={selectedOption}
          onSelectOption={selectOption}
          onApply={handleResolutionApply}
          onSkip={handleResolutionSkip}
          isLoading={isCheckingScope}
        promiseIssues={scopeResult?.issuesDetected}
        issuePlans={scopeResult?.issuePlans}
        />
      </DialogContent>
    </Dialog>
  );
};
